/**
 * Plan Policy — the single, declarative source for what a plan means to the
 * /config page.
 *
 * Everything plan-shaped about configuration lives here: which capabilities a
 * plan unlocks, which config sections those capabilities put on the page, and
 * which config fields/lists they make mandatory. It EXTENDS the plan source of
 * truth in `../common/org-plan` (`OrgPlanSchema`, `PLAN_FEATURES`, `isOrgPlan`,
 * `DEFAULT_ORG_PLAN`) rather than duplicating plan detection: capabilities are
 * derived from `PLAN_FEATURES`, so a plan can never disagree with itself.
 *
 * Importable from BOTH the web app and the server application layer (api-client
 * carries no domain/NestJS imports), so plan-adaptive behavior — org-facts
 * injection on the server, section visibility + save gating on the web /config
 * page — reads one shared policy.
 *
 * THE INVARIANT THIS FILE EXISTS TO HOLD: a config section the plan hides can
 * never produce a validation issue. Visibility and validation are both derived
 * from the tables below, so they cannot drift the way two hand-maintained lists
 * do. (They did: `documentsColumns` used to be gated on the CALENDAR capability,
 * so a `standard` org — calendar yes, documents no — got an unfixable "Add at
 * least one document column" issue pointing at a hidden editor.)
 *
 * Plan definitions are PROVISIONAL. Changing what a plan requires or shows is
 * meant to be a one-line edit in one of the three tables below (or a new entry
 * in `PLAN_FEATURES`); nothing else in the codebase hardcodes plan names for
 * this behavior.
 */

import {
	DEFAULT_ORG_PLAN,
	isOrgPlan,
	type OrgFeatures,
	type OrgPlan,
	PLAN_FEATURES,
} from "../common/org-plan";
import { CONFIG_TAB_IDS, type ConfigTabId } from "../feature-keys";

// ============================================================================
// Capabilities
// ============================================================================

/**
 * A plan-gated capability — one flag of {@link OrgFeatures}. Keyed off the
 * feature-flag shape so adding a feature there makes it gateable here with no
 * extra union to maintain.
 */
export type PlanCapability = keyof OrgFeatures;

/**
 * Every capability name, derived at runtime from the feature matrix so the list
 * cannot drift from `OrgFeatures`.
 */
export const PLAN_CAPABILITIES = Object.keys(
	PLAN_FEATURES[DEFAULT_ORG_PLAN]
) as PlanCapability[];

/**
 * A capability requirement: the capabilities that unlock a thing, ANY-of. An
 * empty list means "unlocked on every plan".
 */
type CapabilityRequirement = readonly PlanCapability[];

/** True when `capabilities` satisfies `required` (ANY-of; empty = always). */
function meetsRequirement(
	capabilities: Record<PlanCapability, boolean>,
	required: CapabilityRequirement
): boolean {
	return (
		required.length === 0 ||
		required.some((capability) => capabilities[capability])
	);
}

// ============================================================================
// The three gating tables (provisional — one-line edits)
// ============================================================================

/**
 * Config section → the capabilities that put it on the page (ANY-of). A section
 * absent from this map is available on every plan (`business`, `ai-assistant`,
 * `appearance`, `whatsapp`, …), subject to the per-tab permission gate the
 * caller applies separately.
 */
const CONFIG_SECTION_CAPABILITIES: Partial<
	Record<ConfigTabId, CapabilityRequirement>
> = {
	schedule: ["calendar"],
	"event-types": ["calendar"],
	"calendar-display": ["calendar"],
	"notification-rules": ["calendar"],
	// The Table Columns tab hosts the calendar-grid AND documents-grid column
	// editors, so EITHER capability keeps it on the page. Which of the two
	// editors renders is decided per column list — see REQUIRED_LIST_FIELDS.
	columns: ["calendar", "documents"],
};

/**
 * Config field paths that must be populated, and the capabilities that make
 * them mandatory.
 *
 * The always-required block is everything the assistant cannot operate without:
 * the business NAME (it introduces itself with it and every notification signs
 * off with it), the country prefix every inbound phone number is normalised
 * against, the timezone that stamps every message and reminder, and `llmModel`
 * — the model the assistant actually runs on; there is no usable configuration
 * without a model picked.
 *
 * The LOCATION facts (address, floor/unit, landmark, map link) are deliberately
 * NOT required. They are injected into the assistant's org-facts prompt block
 * when present, but plenty of businesses have no address a customer would ever
 * visit (online-only, mobile, home-based), and blocking every save on them made
 * the whole config page unsaveable for those orgs.
 *
 * Scheduling adds its own mandatory set once `calendar` is unlocked: availability
 * cannot be rendered without working days + default hours, and a booking cannot
 * be made without at least one event type.
 *
 * Each field's operator-facing "why" lives next to the /config validator
 * (`config-field-requirements.ts` in the web app) — this table only decides
 * WHICH fields a plan demands.
 */
const REQUIRED_CONFIG_FIELDS: readonly {
	capabilities: CapabilityRequirement;
	field: string;
}[] = [
	{ field: "businessName", capabilities: [] },
	// No business-location field appears here on purpose — address, floor/unit,
	// landmark, and the Google Maps link are all optional (see the note above).
	{ field: "defaultCountryPrefix", capabilities: [] },
	{ field: "timezone", capabilities: [] },
	{ field: "llmModel", capabilities: [] },
	{ field: "workingDays", capabilities: ["calendar"] },
	{ field: "defaultWorkingHours", capabilities: ["calendar"] },
	{ field: "eventTypes", capabilities: ["calendar"] },
];

/**
 * List-valued config fields that must be non-empty, and the capability that
 * owns the editor for each. A list whose capability the plan does not unlock is
 * neither required NOR validated — its editor is not on the page, so an issue
 * against it would be unfixable.
 */
const REQUIRED_LIST_FIELDS: readonly {
	capabilities: CapabilityRequirement;
	field: string;
}[] = [
	{ field: "availableLanguages", capabilities: [] },
	{ field: "calendarColumns", capabilities: ["calendar"] },
	{ field: "documentsColumns", capabilities: ["documents"] },
];

// ============================================================================
// Policy
// ============================================================================

export interface PlanPolicy {
	/** Every config section this plan puts on the page. */
	availableConfigSections: ConfigTabId[];
	/** Capability → unlocked, derived from `PLAN_FEATURES` (single source). */
	capabilities: Record<PlanCapability, boolean>;
	/** Config sections this plan locks — the complement of the available set. */
	hiddenConfigSections: ConfigTabId[];
	/**
	 * Config field paths the /config save button gates on: save is blocked while
	 * any of these is empty for the org's current plan.
	 */
	requiredConfigFields: string[];
	/**
	 * List-valued config fields that must be non-empty for this plan. Doubles as
	 * "which list editors are in play", since a list is required exactly when its
	 * editor is on the page.
	 */
	requiredListFields: string[];
}

/** Derive the capability map for a plan from the feature source of truth. */
function capabilitiesForPlan(plan: OrgPlan): Record<PlanCapability, boolean> {
	const features = PLAN_FEATURES[plan];
	return Object.fromEntries(
		PLAN_CAPABILITIES.map((capability) => [capability, features[capability]])
	) as Record<PlanCapability, boolean>;
}

/** Build the full policy for a plan from its capabilities. */
function policyForPlan(plan: OrgPlan): PlanPolicy {
	const capabilities = capabilitiesForPlan(plan);
	const availableConfigSections = CONFIG_TAB_IDS.filter((section) =>
		meetsRequirement(capabilities, CONFIG_SECTION_CAPABILITIES[section] ?? [])
	);
	const availableSet = new Set<string>(availableConfigSections);
	return {
		availableConfigSections,
		capabilities,
		hiddenConfigSections: CONFIG_TAB_IDS.filter(
			(section) => !availableSet.has(section)
		),
		requiredConfigFields: REQUIRED_CONFIG_FIELDS.filter((entry) =>
			meetsRequirement(capabilities, entry.capabilities)
		).map((entry) => entry.field),
		requiredListFields: REQUIRED_LIST_FIELDS.filter((entry) =>
			meetsRequirement(capabilities, entry.capabilities)
		).map((entry) => entry.field),
	};
}

/**
 * The policy for every known plan, derived from `PLAN_FEATURES`. Adding a plan
 * there flows through here automatically. Built once at module load, so each
 * policy object is referentially stable — safe to pass straight into a React
 * dependency array.
 */
export const PLAN_POLICIES: Record<OrgPlan, PlanPolicy> = Object.fromEntries(
	(Object.keys(PLAN_FEATURES) as OrgPlan[]).map((plan) => [
		plan,
		policyForPlan(plan),
	])
) as Record<OrgPlan, PlanPolicy>;

/**
 * Resolve the policy for an arbitrary plan string. Unknown or future plans fall
 * back to the safe minimal default plan's policy (lowest tier: no calendar /
 * documents capabilities, only the base required fields).
 */
export function getPlanPolicy(plan: string): PlanPolicy {
	return isOrgPlan(plan)
		? PLAN_POLICIES[plan]
		: PLAN_POLICIES[DEFAULT_ORG_PLAN];
}

/**
 * Whether a config section is on the page for this policy. Sections outside the
 * shared `CONFIG_TAB_IDS` catalog (nav-only aliases such as `subagents`) are
 * never plan-gated and always resolve to `true` — the caller applies its own
 * permission/expert-mode gates on top.
 */
export function isConfigSectionAvailable(
	policy: PlanPolicy,
	sectionId: string
): boolean {
	if (!(CONFIG_TAB_IDS as readonly string[]).includes(sectionId)) {
		return true;
	}
	return policy.availableConfigSections.includes(sectionId as ConfigTabId);
}

/** Whether a list-valued config field is in play (required + validated) here. */
export function isRequiredListField(
	policy: PlanPolicy,
	field: string
): boolean {
	return policy.requiredListFields.includes(field);
}
